import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { accountsAPI, webhookAPI } from '../services/api';
import toast from 'react-hot-toast';

interface ManualTradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ManualTradeModal: React.FC<ManualTradeModalProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    action: 'BUY',
    symbol: '',
    exchange: 'NSE',
    quantity: '',
    order_type: 'MARKET',
    product: 'MIS',
    price: '',
    accounts: [] as number[],
    override_amount: '', // Optional override for position size
  });

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsAPI.list().then((res) => res.data),
  });

  const tradeMutation = useMutation({
    mutationFn: (data: any) => webhookAPI.send(data),
    onSuccess: () => {
      toast.success('Trade executed successfully');
      queryClient.invalidateQueries({ queryKey: ['trades'] });
      queryClient.invalidateQueries({ queryKey: ['tradeStats'] });
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to execute trade');
    },
  });

  const resetForm = () => {
    setFormData({
      action: 'BUY',
      symbol: '',
      exchange: 'NSE',
      quantity: '',
      order_type: 'MARKET',
      product: 'MIS',
      price: '',
      accounts: [],
      override_amount: '',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Build payload
    const payload: any = {
      action: formData.action,
      symbol: formData.symbol.toUpperCase(),
      exchange: formData.exchange,
      order_type: formData.order_type,
      product: formData.product,
    };

    // Add quantity or override amount
    if (formData.override_amount) {
      // If override amount is provided, calculate quantity from it
      payload.buy_price = parseFloat(formData.price) || undefined;
      payload.override_position_size = parseFloat(formData.override_amount);
    } else if (formData.quantity) {
      payload.quantity = parseInt(formData.quantity);
    }

    // Add price for limit orders
    if (formData.order_type === 'LIMIT' && formData.price) {
      payload.price = parseFloat(formData.price);
    }

    // Add selected accounts
    if (formData.accounts.length > 0) {
      payload.accounts = formData.accounts;
    }

    tradeMutation.mutate(payload);
  };

  const toggleAccount = (accountId: number) => {
    setFormData((prev) => ({
      ...prev,
      accounts: prev.accounts.includes(accountId)
        ? prev.accounts.filter((id) => id !== accountId)
        : [...prev.accounts, accountId],
    }));
  };

  const selectAllAccounts = () => {
    const allAccountIds = accounts?.map((acc: any) => acc.id) || [];
    setFormData((prev) => ({
      ...prev,
      accounts: prev.accounts.length === allAccountIds.length ? [] : allAccountIds,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">Manual Trade</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Action and Symbol */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Action *
              </label>
              <select
                value={formData.action}
                onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              >
                <option value="BUY">BUY</option>
                <option value="SELL">SELL</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Symbol *
              </label>
              <input
                type="text"
                value={formData.symbol}
                onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                placeholder="e.g., SBIN"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* Exchange and Product */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Exchange *
              </label>
              <select
                value={formData.exchange}
                onChange={(e) => setFormData({ ...formData, exchange: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              >
                <option value="NSE">NSE</option>
                <option value="BSE">BSE</option>
                <option value="NFO">NFO</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product *
              </label>
              <select
                value={formData.product}
                onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              >
                <option value="MIS">MIS (Intraday)</option>
                <option value="CNC">CNC (Delivery)</option>
                <option value="NRML">NRML (F&O)</option>
              </select>
            </div>
          </div>

          {/* Order Type and Price */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order Type *
              </label>
              <select
                value={formData.order_type}
                onChange={(e) => setFormData({ ...formData, order_type: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              >
                <option value="MARKET">MARKET</option>
                <option value="LIMIT">LIMIT</option>
              </select>
            </div>

            {formData.order_type === 'LIMIT' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="e.g., 625.50"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required={formData.order_type === 'LIMIT'}
                />
              </div>
            )}
          </div>

          {/* Quantity or Override Amount */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-900 mb-3">
              Choose one: Specify quantity OR override position size
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value, override_amount: '' })}
                  placeholder="e.g., 10"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  disabled={!!formData.override_amount}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Override Amount (₹)
                </label>
                <input
                  type="number"
                  step="100"
                  value={formData.override_amount}
                  onChange={(e) => setFormData({ ...formData, override_amount: e.target.value, quantity: '' })}
                  placeholder="e.g., 10000"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  disabled={!!formData.quantity}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Overrides default position size
                </p>
              </div>
            </div>
          </div>

          {/* Account Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Accounts (leave empty for all active)
              </label>
              <button
                type="button"
                onClick={selectAllAccounts}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {formData.accounts.length === accounts?.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {accounts?.map((account: any) => (
                <label
                  key={account.id}
                  className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition ${
                    formData.accounts.includes(account.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${!account.is_active || !account.is_authenticated ? 'opacity-50' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={formData.accounts.includes(account.id)}
                    onChange={() => toggleAccount(account.id)}
                    disabled={!account.is_active || !account.is_authenticated}
                    className="mr-3"
                  />
                  <div>
                    <p className="font-medium text-sm">{account.account_name}</p>
                    <p className="text-xs text-gray-500">{account.kite_user_id}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={tradeMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {tradeMutation.isPending ? 'Executing...' : 'Execute Trade'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualTradeModal;
